import dayjs from "dayjs"
import { useMemo } from "react"
import { useGraphContext } from "../GraphContext"
import { useDisplayDividerTime } from "./useDisplayDividerTime"
import {
  useDisplayTime,
  useThisMinute,
} from "src/hooks"

/*
 * Approximate width in pixels of a single character in the time labels at
 * font-size-xs (0.75rem = 12px). Intentionally generous so the collision test
 * errs toward suppression: the labels should never visibly overlap, and at
 * worst we hide a divider label that had a little room to spare.
 */
const approximateCharacterWidth = 12 * 0.62

/*
 * Extra breathing room (in pixels) required between the now label and a divider
 * label before we consider them non-overlapping.
 */
const labelPadding = 4

/*
 * Returns the ISO time of the single time divider whose label should be hidden
 * because it would visually overlap the "now" time label, or `undefined` when
 * no divider collides.
 *
 * Only the last labeled divider can ever collide, because time dividers are
 * filtered to times before the current minute (see `useTimeDividers`), so the
 * now line is always the right-most marker in its graph item.
 */
export function useSuppressedTimeDivider(
  start: string,
  timeDividers: string[],
): string | undefined {
  const {
    pointsPerHour,
    dividersPerTimeLabel,
    hoursPerGraphItem,
  } = useGraphContext()

  const thisMinute = useThisMinute()
  const displayTime = useDisplayTime()
  const displayDividerTime = useDisplayDividerTime()

  return useMemo(
    () => {
      if (timeDividers.length === 0) {
        return undefined
      }

      // "now" must fall inside this graph item for the now label to exist here.
      const nowOffsetInHours = dayjs(thisMinute).diff(
        start,
        "hours",
        true,
      )
      if (nowOffsetInHours < 0 || nowOffsetInHours >= hoursPerGraphItem) {
        return undefined
      }
      const nowX = nowOffsetInHours * pointsPerHour

      // The index of the last divider that actually renders a label.
      const lastLabeledIndex = Math.floor(
        (timeDividers.length - 1) / dividersPerTimeLabel,
      ) * dividersPerTimeLabel
      const dividerTime = timeDividers[lastLabeledIndex]
      const dividerX = dayjs(dividerTime).diff(
        start,
        "hours",
        true,
      ) * pointsPerHour

      /*
       * Two centered labels overlap when the gap between their centers is
       * smaller than the sum of their half-widths (plus a little padding). The
       * now label always shows HH:MM; the divider label may be hour-only, so
       * estimate each separately rather than assuming they are the same width.
       */
      const nowHalfWidth = (
        displayTime(thisMinute).length * approximateCharacterWidth
      ) / 2
      const dividerHalfWidth = (
        displayDividerTime(dividerTime).length * approximateCharacterWidth
      ) / 2
      const threshold = nowHalfWidth + dividerHalfWidth + labelPadding

      return Math.abs(nowX - dividerX) < threshold ? dividerTime : undefined
    },
    [
      start,
      timeDividers,
      thisMinute,
      pointsPerHour,
      dividersPerTimeLabel,
      hoursPerGraphItem,
      displayTime,
      displayDividerTime,
    ],
  )
}
